import React, { FunctionComponent, useMemo } from "react";
import { NutrientSource } from "../../../api/smartmelts-api/graphql/generated";
import { Grid } from "@mui/material";
import { SubheadingText } from "../../text/subheading-text";
import { ContentText } from "../../text/content-text";
import { makeStyles, Theme } from "@material-ui/core";
import { PlantBasedIcon } from "../../../assets/icons/diet/plant-based-icon";
import { AnimalBasedIcon } from "../../../assets/icons/diet/animal-based-icon";
import { useTranslation } from "react-i18next";
import { PRIMARY_COLOR } from "../../../theme/theme";
import { DairyBasedIcon } from "../../../assets/icons/diet/dairy-based-icon";
import { FishBasedIcon } from "../../../assets/icons/diet/fish-based-icon";

const PLANT_BASED_BADGE_COLOR = "#9ABD76";
const DAIRY_BASED_BADGE_COLOR = "#fffdd0";
const FISH_BASED_BADGE_COLOR = PRIMARY_COLOR;
const MEAT_BASED_BADGE_COLOR = "#EE6464";

const DIET_BADGE_SETTINGS = {
  standard: {
    badgeColor: MEAT_BASED_BADGE_COLOR,
    iconColor: "white",
    icon: AnimalBasedIcon,
  },
  pescatarian: {
    badgeColor: FISH_BASED_BADGE_COLOR,
    iconColor: "white",
    icon: FishBasedIcon,
  },
  vegetarian: {
    badgeColor: DAIRY_BASED_BADGE_COLOR,
    iconColor: "black",
    icon: DairyBasedIcon,
  },
  vegan: {
    badgeColor: PLANT_BASED_BADGE_COLOR,
    iconColor: "white",
    icon: PlantBasedIcon,
  },
};
interface INutrientSourceBlockStyleProps {
  url: string;
  badgeColor: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  circle: {
    backgroundImage: ({ url }: INutrientSourceBlockStyleProps) => `url("${url}")`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    boxShadow: "0px 0px 10px black",
  },
  badge: {
    backgroundColor: ({ badgeColor }: INutrientSourceBlockStyleProps) => badgeColor,
    position: "absolute",
    bottom: 0,
    right: 0,
    width: "28px",
    height: "28px",
    padding: 4,
    borderRadius: "50%",
    boxShadow: "0px 0px 10px black",
  },
  sourceImgContainer: {
    position: "relative",
    paddingLeft: theme.spacing(3),
  },
  sourceName: {
    paddingLeft: theme.spacing(3),
  },
}));

interface INutrientSourceBlockProps {
  source: Omit<NutrientSource, "__typename">;
  index: number;
}

export const NutrientSourceBlock: FunctionComponent<INutrientSourceBlockProps> = (props) => {
  const { source, index } = props;
  const { t } = useTranslation();
  const dietSetting = useMemo(() => {
    const { isVegan, isVegeterian, isPescatarian } = source;
    return isVegan
      ? DIET_BADGE_SETTINGS.vegan
      : isVegeterian
      ? DIET_BADGE_SETTINGS.vegetarian
      : isPescatarian
      ? DIET_BADGE_SETTINGS.pescatarian
      : DIET_BADGE_SETTINGS.standard;
  }, [source]);

  const classes = useStyles({
    url: `/images/nutrient-source/${source.name
      .toLowerCase()
      .replaceAll(/([-\s])/gi, "_") // replace spaces with _
      .replaceAll(/([^\w])/gi, "")}.jpg`, // delete not alphanumeric or space.
    badgeColor: dietSetting.badgeColor,
  });

  const BadgeIcon = useMemo(() => dietSetting.icon, [dietSetting]);

  return (
    <Grid container alignItems="center" wrap="nowrap">
      <Grid item xs={1}>
        <SubheadingText>{index}</SubheadingText>
      </Grid>
      <Grid item className={classes.sourceImgContainer}>
        <Grid item className={classes.circle} />
        <Grid item className={classes.badge}>
          <BadgeIcon color={dietSetting.iconColor} />
        </Grid>
      </Grid>
      <Grid item className={classes.sourceName}>
        <ContentText small light>
          {t(`nutrient.source_values.${source.name}`)}
        </ContentText>
      </Grid>
    </Grid>
  );
};
